// CSC-ONLY proposal pipeline. Extracted from cscProposalTab.jsx so the new
// Combined tab and the old Proposal tab share ONE source of truth for the
// commercial 29-page proposal. COMMERCIAL math/markup only — must NOT import
// or reuse any RSC code (useRscPricing saving math / proposalEngine.js).
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { CSC_PROPOSAL_TARIFFS, sareBuybackFactor, SARE_BUYBACK_FACTORS } from '../../hooks/useCscProposalMath';
import { sstOn } from '../../utils/sst';
import { warrantyYears, warrantyHasCleaning } from '../../utils/cscSizing';
import { chartDayProfile, chartMonthlyMd, chartPaybackByUnit, chartShavedByUnit, chartCashflow }
    from '../../utils/cscBessCharts';

const fmt = (n) => Number(n || 0).toLocaleString('en-MY', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
// SST is shown to 2 decimals (exact, not whole-ringgit) — user 2026-08-04.
const fmt2 = (n) => Number(n || 0).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmt3 = (n) => Number(n || 0).toLocaleString('en-MY', { minimumFractionDigits: 3, maximumFractionDigits: 3 });

const fmtDMY = (d) => {
    const dt = d instanceof Date ? d : new Date(d);
    return `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}/${dt.getFullYear()}`;
};
export const todayDMY = () => fmtDMY(new Date());
export const plus30DMY = () => { const d = new Date(); d.setDate(d.getDate() + 30); return fmtDMY(d); };

// Unified inverter sizing rule (per spec, 2026-05-20):
//   ≤26 kWp   → look up ATAP Residential Pricing row (phase-specific
//               inverterSize / inverterSize3p); fall back to
//               Math.ceil(kwp/1.35) if no ATAP row matches
//   ~96 kWp   → 72 kW (known product spec: 150 × 640W → GoodWe/Solis 72K)
//   otherwise → smallest 5-kW multiple where kWp/kW < 1.35
// Mirrors autoFillInverter in commercialSolarCalculator.jsx — keep in sync.
export function autoInverterKw(kwp, atapPrices = [], phaseMode = 'single') {
    const k = Number(kwp) || 0;
    if (k <= 0) return 0;
    if (k <= 26) {
        const match = (atapPrices || []).find(p => Math.abs(p.kwp - k) < 0.05);
        if (match) {
            const str = phaseMode === '3phase' ? match.inverter3p : match.inverter;
            const m = /(\d+(?:\.\d+)?)\s*k/i.exec(String(str || ''));
            if (m) return Number(m[1]);
        }
        return Math.ceil(k / 1.35);
    }
    if (k > 95 && k <= 96.1) return 72;
    return Math.ceil(k / 1.35 / 5) * 5;
}

// SST = 6% on the 20% portion of the system price — via the single canonical
// helper in utils/sst.js (same round-off as the commercial quotation + every
// other SST path). Effective rate on the full price = 1.2%.

// Malaysia state → Global Horizontal Irradiance (kWh/m²/day, SolarGIS map).
// Drives the Site Solar Data page. PSH (peak sun hours) is held at 3.4 to
// match the Bill tab / commercial calculator (useCscProposalMath.PEAK_SUN_HOURS),
// so customers see both the geographic GHI *and* the conservative PSH the
// projection actually uses.
export const MY_STATE_GHI = {
    'perlis':           { name: 'Perlis',           ghi: 4.6 },
    'kedah':            { name: 'Kedah',            ghi: 4.6 },
    'penang':           { name: 'Penang',           ghi: 4.4 },
    'perak':            { name: 'Perak',            ghi: 4.5 },
    'selangor':         { name: 'Selangor',         ghi: 4.4 },
    'kl':               { name: 'Kuala Lumpur',     ghi: 4.4 },
    'putrajaya':        { name: 'Putrajaya',        ghi: 4.4 },
    'n9':               { name: 'Negeri Sembilan',  ghi: 4.6 },
    'melaka':           { name: 'Melaka',           ghi: 4.6 },
    'johor':            { name: 'Johor',            ghi: 4.4 },
    'pahang':           { name: 'Pahang',           ghi: 4.6 },
    'terengganu':       { name: 'Terengganu',       ghi: 4.8 },
    'kelantan':         { name: 'Kelantan',         ghi: 4.6 },
    'sabah':            { name: 'Sabah',            ghi: 4.8 },
    'sarawak':          { name: 'Sarawak',          ghi: 4.8 },
    'labuan':           { name: 'Labuan',           ghi: 4.8 },
};
const PERFORMANCE_RATIO = 0.8499;  // 84.99% — matches the irradiance page reference

// ── Build the substitution map from form state + math output ──
// 90% generation guarantee note — shown on the projection-table footnotes ONLY for
// systems 54 kWp and above (user 2026-06-29). Returns '' below the threshold so the
// {{generation_guarantee_note}} placeholder resolves to empty. Centralised here so
// EVERY render path (generateCscProposalPdf AND the direct print/preview paths that
// call cscSubstitute on a buildCscVarMap map) shows it consistently.
export function cscGenerationGuaranteeNote(lang, kwpNum) {
    if ((Number(kwpNum) || 0) < 54) return '';
    return lang === 'cn' ? '<br><strong>* 发电保证：</strong>所示太阳能发电量（kWh）在整个年限内保证达到 90%。'
         : lang === 'my' ? '<br><strong>* Jaminan penjanaan:</strong> 90% daripada penjanaan solar (kWh) yang ditunjukkan dijamin sepanjang tahun.'
         : '<br><strong>* Generation guarantee:</strong> 90% of the solar generation (kWh) shown is guaranteed throughout the years.';
}

export function buildCscVarMap({ customer, system, math, sales, dates, financing, addOn, site, photos, maxDemand, batteryKwh, batteryBrand = 'goodwe', summarySavingMo, kwpLabelOverride, panelCountOverride, addons, savingSplits, yearBills = null, lang = 'en' }) {
    // Multi-meter override: when N meters are combined, the cover shows the
    // breakdown label ("30+40") and the SUMMED panel count instead of a single
    // derived value. Single-meter callers pass neither → existing behaviour.
    // Summary headline saving = REAL bill reduction passed in (Option B). Falls back to
    // the generation-revenue model when not provided. The 20-year TABLE is unaffected.
    const _summaryMo = (summarySavingMo != null && summarySavingMo > 0)
        ? summarySavingMo
        : ((math.years?.[0]?.grossRevenue || 0) / 12);
    // tariffKey = the tariff OPTION picked (0.303 = Medium Voltage). For MV, tariffRate is the
    // solar-weighted energy rate (≈0.29), which matches no option, so look the label up by key.
    const tariffEntry = CSC_PROPOSAL_TARIFFS.find((t) => t.rate === (system.tariffKey ?? system.tariffRate));
    // Honor user override from the proposal form; otherwise fall back to
    // the tier-based auto-size (matches the sizing tab's INVERTER_TIERS).
    const inverterKw = Number(system.inverterKw) > 0
        ? Number(system.inverterKw)
        : autoInverterKw(system.kwp, system.atapPrices, system.phaseMode);
    const panelWatt = Number(system.panelWatt) || 650;
    const panelCount = (panelCountOverride != null && panelCountOverride > 0)
        ? panelCountOverride
        : Math.ceil((Number(system.kwp) || 0) * 1000 / panelWatt);
    // System-size label: combined "30+40" when overridden, else the numeric kWp.
    const kwpLabel = (kwpLabelOverride != null && String(kwpLabelOverride).trim() !== '')
        ? String(kwpLabelOverride)
        : (system.kwp || '—');
    // Multi-select inverter brand (per user, 2026-05-21). Falls back to the
    // single inverterBrand string, then to a kWp-derived default.
    const BRAND_LABELS = { solis: 'Solis', goodwe: 'GoodWe', huawei: 'Huawei' };
    const brandsList = Array.isArray(system.inverterBrands) && system.inverterBrands.length > 0
        ? system.inverterBrands
        : (system.inverterBrand ? [system.inverterBrand] : []);
    const mappedBrands = brandsList
        .map(b => BRAND_LABELS[String(b || '').toLowerCase()])
        .filter(Boolean);
    // Inverter descriptor mirrors the quotation's InvScopeLabel: a hybrid system is
    // simply "GoodWe Hybrid". The old ">20 kW + string inverter" mix label dated from a
    // 20 kW hybrid ceiling; hybrids now reach 60 kW (stacked PCS) and larger systems are
    // String already. Dropped 2026-08-20 (user).
    // Kept in lockstep with QuotationPdfService.InvScopeLabel — if one changes, change both.
    // String systems (Solis / Huawei) keep "{Brand} String". (user 2026-08-06)
    const _primaryBrand = String(brandsList[0] || '').toLowerCase();
    const _isHybridInv = _primaryBrand === 'goodwe'
        || (Number(batteryKwh) > 0 && _primaryBrand !== 'solis' && _primaryBrand !== 'huawei');
    const _invKwNum = Number(inverterKw) || 0;
    // Preferred: the card's (or each meter's) REAL inverter brand + type, e.g. "Huawei String",
    // "GoodWe String", or "GoodWe Hybrid / Huawei String" on a mixed multi-meter job. The guess
    // below printed "GoodWe Hybrid" for every GoodWe inverter (String included) and, on
    // multi-meter, for Huawei meters too. (user 2026-09-22) The guess is kept only for the
    // standalone Proposal tab, which has no type to pass.
    const _pairs = Array.isArray(system.inverterPairs)
        ? system.inverterPairs.filter(p => p && BRAND_LABELS[String(p.brand || '').toLowerCase()])
        : [];
    // Huawei has no String/Hybrid split — a battery attaches through a power module, so the
    // type is meaningless there and printing "Huawei String" is wrong. GoodWe keeps the split.
    // (user 2026-09-26)
    const _pairLabel = _pairs.length > 0
        ? [...new Set(_pairs.map(p => {
            const brand = String(p.brand).toLowerCase();
            const label = BRAND_LABELS[brand];
            if (brand === 'huawei') return label;
            return `${label} ${String(p.type || '').toLowerCase() === 'hybrid' ? 'Hybrid' : 'String'}`;
        }))].join(' / ')
        : '';
    const inverterBrandLabel = _pairLabel || (mappedBrands.length > 0
        ? (_isHybridInv
            ? 'GoodWe Hybrid'
            : mappedBrands.map(b => `${b} String`).join(' / '))
        : ((Number(system.kwp) || 0) >= 100 ? 'Huawei' : 'GoodWe / Solis'));
    // BoM table renders panel count separately in the Solar Panel row; this
    // spec is only the inverter line — just size + brand, no redundant panels.
    const inverterSpec = `${inverterKw} kW ${inverterBrandLabel}`;

    // Optional battery storage. If set, adds a BoM row + a fragment on the
    // cover system-spec line so the customer sees the storage capacity.
    const batKwh = Number(batteryKwh) || 0;
    // Huawei (user 2026-09-21) is sold in 241-kWh blocks, so its quantity reads "482 kWh (2 × 241 kWh)".
    const _huaweiBatt = String(batteryBrand || '').toLowerCase() === 'huawei';
    const _huaweiBlocks = _huaweiBatt ? Math.max(1, Math.round(batKwh / 241)) : 0;
    const batteryBomRow = batKwh > 0
        ? (_huaweiBatt
            ? `<tr><td class="product">Lithium Battery (LiFePO4) — High-Voltage Commercial</td><td class="brand">Huawei</td><td class="qty">${batKwh} kWh${_huaweiBlocks > 1 ? ` (${_huaweiBlocks} × 241 kWh)` : ''}</td></tr>`
            : `<tr><td class="product">Lithium Battery (LiFePO4)</td><td class="brand">GoodWe Lynx / Huawei LUNA / equivalent Tier-1</td><td class="qty">${batKwh} kWh</td></tr>`)
        : '';
    const batteryCoverFrag = batKwh > 0
        ? ` · <span class="sub" style="display:inline;margin-left:1mm;">+ ${batKwh} kWh battery</span>`
        : '';
    // 10-year battery PRODUCT WARRANTY card — only when the system carries a
    // battery. Matches the existing .warranty-card markup; the template chooses
    // the year-class div by language (.years / .年 / .tahun) via {{battery_warranty_card_*}}.
    const batteryWarrantyCard = (cls, label, item) => batKwh > 0
        ? `<div class="warranty-card"><div class="${cls}">10 yr</div><div class="label">${label}</div><div class="item">${item}</div></div>`
        : '';

    // Catalogue add-ons (optimizers, etc. the agent added) → one BoM <tr> each.
    // `addons` is [{ name, qty, brand? }]; undefined / empty → no extra rows
    // (backward-compatible). Qty falls back to 1; brand cell left blank when
    // not supplied. HTML-escaped so a stray < or & can't break the table.
    const escHtml = (s) => String(s == null ? '' : s)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const addonBomRows = (Array.isArray(addons) ? addons : [])
        .map((a) => ({ name: escHtml((a && (a.name || a.itemName) || '')).trim(), qty: Number(a && (a.qty ?? a._qty)) || 1, brand: escHtml(a && a.brand || '').trim() }))
        .filter((a) => a.name)
        .map((a) => `<tr><td class="product">${a.name}</td><td class="brand">${a.brand}</td><td class="qty">${a.qty}</td></tr>`)
        .join('\n      ');

    // Signing deposit / booking — driven by INVERTER SIZE (user 2026-07):
    // inverter ≤ 72 kW → RM 2,000; > 72 kW → RM 5,000. (Was kWp-based: >97 kWp → 5,000.)
    // Applies to Outright, Loan, and the Comparison table. For MULTI-METER, use the LARGEST
    // SINGLE-METER inverter (system.depositInverterKw), NOT the combined sum — a 40+42=82 kW
    // multi-meter stays at RM 2,000; only a single meter > 72 kW triggers RM 5,000. Single-calc
    // has no depositInverterKw → falls back to the system inverter. (user 2026-08-03)
    const depositInvKw = Number(system?.depositInverterKw ?? inverterKw) || 0;
    const depositAmount = depositInvKw > 72 ? 5000 : 2000;

    // Add-on + SST (per user, 2026-05-21): add-on is an optional surcharge
    // (extra services, scope additions) added on top of the system price.
    // SST follows the commercial quotation method: 6% on the 20% portion.
    const baseSystemPrice = Number(system.systemCost) || 0;
    const addOnAmount = Number(addOn) || 0;
    const subtotal = baseSystemPrice + addOnAmount;
    const sstAmount = sstOn(subtotal);
    const grandTotalPrice = subtotal + sstAmount;

    // Site irradiance: state-based GHI from the SolarGIS Malaysia map.
    // PSH is 3.4 (aligned to Bill tab / Calc); the page surfaces both numbers
    // so the customer sees the geographic upside vs. the value used in projection.
    const stateInfo = (site && MY_STATE_GHI[site]) || { name: '—', ghi: 4.5 };
    const sitePsh = 3.4;

    // Site photo grid — convert the array of data URLs into <img> tags for
    // the conditional "Site Design Summary" page.
    const photoList = Array.isArray(photos) ? photos.filter(Boolean) : [];
    const photosHtml = photoList.map((src, i) =>
        `<div class="photo-cell"><img src="${src}" alt="Site photo ${i + 1}"></div>`
    ).join('\n');

    // SELCO vs ATAP recommendation (corrected 2026-05-21):
    //   inverter kW > Maximum Demand (kW) → SELCO (limit export / 0 export)
    //   inverter kW ≤ Maximum Demand     → ATAP (sells excess at SMP)
    // Trigger is the AC inverter rating, not DC panel kWp — what TNB cares
    // about is the actual export capacity, which is gated by the inverter.
    const md = Number(maxDemand) || 0;
    const invForCheck = Number(inverterKw) || 0;
    let schemeName, schemeBlurb, schemeColor;
    if (md > 0 && invForCheck > md) {
        schemeName = 'SELCO';
        schemeBlurb = `Your inverter output (${invForCheck} kW) exceeds your Maximum Demand (${md} kW). Under TNB rules, the system runs as <strong>SELCO</strong> (Self-Consumption) with a <strong>limit export device</strong> / <strong>zero export</strong> setup — all generation is consumed on-site, none is exported to the grid.`;
        schemeColor = 'gold';
    } else if (md > 0) {
        schemeName = 'ATAP';
        schemeBlurb = `Your inverter output (${invForCheck} kW) is within your Maximum Demand (${md} kW). The system qualifies for <strong>ATAP</strong> — any excess generation exported to the grid is sold to TNB at the prevailing <strong>System Marginal Price (SMP)</strong> rate.`;
        schemeColor = 'blue';
    } else {
        schemeName = 'ATAP';
        schemeBlurb = `<em>Maximum Demand not specified.</em> Default assumption: <strong>ATAP</strong> — excess generation sold to TNB at SMP rate. If site MD &lt; inverter output, the project switches to <strong>SELCO</strong> (zero export).`;
        schemeColor = 'grey';
    }

    const m = math?.invalid ? {} : (math || {});

    // SARE projection table rows — TNB tariff escalates 8% every 3 years,
    // SARE rate stays flat; discount widens over time.
    //
    // Buyback = revised capex schedule (Astern Commercial Calculation Sheet, SPPA sheet
    // "Revised Buyback" O7:O26, user 2026-09-19): year 1 = capex × 1.5 (O7 = Input!I8 × 1.5),
    // then each year = previous year × a fixed factor. It replaces the old
    // NPV(8%, remaining operating income) buyback (column N), so generation, tariff, O&M and
    // the 8% rate no longer move it. A 10-year term shows the same schedule's first 10 years.
    const sareSystemCost = Number(system.systemCost) || 0;
    const sareRateFlat = Number(m.sareTariff) || 0;
    // The schedule and the term-stretch live at module scope (sareBuybackFactor, above),
    // so they can be tested without rendering a proposal.
    const buyback = (yearIdx, termYears) => sareSystemCost * sareBuybackFactor(yearIdx, termYears);
    const yearsArr = m.years || [];

    // Both SARE terms (20-year and 10-year) share one table builder and the same buyback
    // schedule. Only the flat lease rate, the horizon and which per-year block the math hook
    // produced differ. Built once here and called twice so the two terms can never drift
    // apart. (user 2026-08-23)
    function buildSareSeries(rateFlat, sareKey, termYears, showYears = termYears) {
        const rows = yearsArr.filter((y) => Number(y.year) <= showYears);
        const html = rows.map((y, idx) => {
            const inTerm = Number(y.year) <= termYears;
            /* Past the lease term the system is THEIRS: no rate to pay, so the saving is
               the whole tariff rather than the discount off it. The `owned` block carries
               exactly that, on the same generation and the same escalating tariff.
               (user 2026-10-23: "just the saving wll become more, cause they own the
               whole thing already") */
            const s = inTerm ? y[sareKey] : y.owned;
            if (!s) return '';
            const tariffYear = s.tariffYear || y.tariffYear || system.tariffRate;
            const netCF = s.annualNetCF != null ? s.annualNetCF : s.annualCF;
            // Buyback is the price to END the lease early. Once the lease has run its
            // course there is nothing left to buy, so the column reads Owned rather than
            // quoting a price for something they already hold.
            // termYears, so a 10- or 15-year table runs its buyback down to the handover
            // instead of stopping partway along the 20-year curve.
            const buybackCell = inTerm ? `RM ${fmt(buyback(idx, termYears))}` : 'Owned';
            return `
        <tr${inTerm ? '' : ' class="owned-year"'}>
            <td>${y.year}</td>
            <td>${fmt(y.generation)}</td>
            <td>${fmt3(tariffYear)}</td>
            <td>${inTerm ? fmt3(rateFlat) : '—'}</td>
            <td>${fmt3(s.discount ?? (tariffYear - rateFlat))}</td>
            <td>${fmt(s.annualCF)}</td>
            <td>${fmt(netCF / 12)}</td>
            <td>${fmt(s.annualCF / 12)}</td>
            <td>${buybackCell}</td>
        </tr>`;
        }).filter(Boolean).join('\n');
        return { html };
    }

    /* ── The year, month by month ──────────────────────────────────────────────
       Built here as plain HTML so it prints: the proposal is rasterised to PDF, and a
       canvas chart or an external library would arrive as a blank box. Bars are divs.

       Rendered ONLY when the agent supplied real bills. Without them the page is removed
       entirely rather than printed with one month repeated twelve times, which would
       claim a precision nobody gave us. (user 2026-10-23/24) */
    const MONTH_NAMES_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const yb = yearBills && yearBills.count >= 2 ? yearBills : null;
    const ybYear = yb
        ? Array.from({ length: 12 }, (_, i) => ({
            ...yb.months[i % yb.count],
            month: MONTH_NAMES_EN[i],
            projected: i >= yb.count,
        }))
        : [];
    const ybTopBill = yb ? Math.max(...ybYear.map((x) => x.bill)) || 1 : 1;

    const yearBillChart = yb ? ybYear.map((x) => {
        const h = Math.max(4, (x.bill / ybTopBill) * 150);
        const fill = Math.max(0, Math.min(h, h * (x.saving / (x.bill || 1))));
        return `<div class="yb-col">
            <div class="yb-pct">${Math.round(x.pct)}%</div>
            <div class="yb-bar${x.projected ? ' proj' : ''}" style="height:${h}px">
              <div class="yb-fill${x.projected ? ' proj' : ''}" style="height:${fill}px"></div>
            </div>
            <div class="yb-mon">${x.month}${x.projected ? '*' : ''}</div>
          </div>`;
    }).join('') : '';

    const yearBillRows = yb ? ybYear.map((x) => `<tr${x.projected ? ' class="proj"' : ''}>
            <td>${x.month}${x.projected ? ' *' : ''}</td>
            <td>${fmt(x.bill)}</td>
            <td class="save">${fmt(x.saving)}</td>
            <td>${fmt(Math.max(0, x.bill - x.saving))}</td>
            <td>${Math.round(x.pct)}%</td>
          </tr>`).join('') : '';

    const ybTotalBill = yb ? ybYear.reduce((a, x) => a + x.bill, 0) : 0;
    const ybTotalSave = yb ? ybYear.reduce((a, x) => a + x.saving, 0) : 0;

    const sareRateFlat10 = Number(m.sareTariff10) || 0;
    const sareRows = buildSareSeries(sareRateFlat, 'sare', 20).html;
    // The 10-year lease, shown over 20 years: ten years leasing, then ten years owning
    // it outright. Same generation curve throughout — only the lease cost falls away.
    const sare10Rows = buildSareSeries(sareRateFlat10, 'sare10', 10, 20).html;
    // And the 15-year lease, shown the same way: fifteen years leasing, then five owned.
    const sareRateFlat15 = Number(m.sareTariff15) || 0;
    const sare15Rows = buildSareSeries(sareRateFlat15, 'sare15', 15, 20).html;

    // Totals for the two halves and the whole, so the table foots to something real
    // rather than leaving the reader to add twenty rows.
    const netOf = (b) => (b ? (b.annualNetCF != null ? b.annualNetCF : b.annualCF) : 0);
    const sare10LeaseTotal = yearsArr
        .filter((y) => Number(y.year) <= 10)
        .reduce((s, y) => s + netOf(y.sare10), 0);
    const sare10OwnedTotal = yearsArr
        .filter((y) => Number(y.year) > 10 && Number(y.year) <= 20)
        .reduce((s, y) => s + netOf(y.owned), 0);
    const sare15LeaseTotal = yearsArr
        .filter((y) => Number(y.year) <= 15)
        .reduce((s, y) => s + netOf(y.sare15), 0);
    const sare15OwnedTotal = yearsArr
        .filter((y) => Number(y.year) > 15 && Number(y.year) <= 20)
        .reduce((s, y) => s + netOf(y.owned), 0);

    /* Loan-financed cashflow rows (Year | Gen | Savings | CA | GITA | Loan Payment |
       Net Cashflow | Cumulative). Takes the SERIES as an argument because each compared
       term has its own: a 3-year loan's payment column stops in year 3. Feeding one
       series to every term printed the 7-year schedule on the 3-year page.
       (user 2026-10-16: "I think the tenure and all is wrong") */
    const buildLoanRows = (series, breakevenYear) => (series || []).map((y) => `
        <tr${y.year === breakevenYear ? ' class="highlight"' : ''}>
            <td>${y.year}</td>
            <td>${fmt(y.generation)}</td>
            <td>${fmt(y.grossRevenue)}</td>
            <td>${y.caTaxSaving > 0 ? fmt(y.caTaxSaving) : '—'}</td>
            <td>${y.itaTaxSaving > 0 ? fmt(y.itaTaxSaving) : '—'}</td>
            <td>${y.annualPayment > 0 ? fmt(y.annualPayment) : '—'}</td>
            <td>${fmt(y.annualCF)}</td>
            <td>${fmt(y.cumulative)}</td>
        </tr>`).join('\n');

    // The single loan block keeps its own rows, shaped the same way.
    const loanRows = buildLoanRows(
        (m.years || []).map((y) => ({ ...y, ...y.loan })), m.loanBreakeven);

    // Outright projection table rows (Year | Gen | Tariff | Savings | Monthly | CA | GITA | Net Return)
    const outrightRows = (m.years || []).map((y) => `
        <tr${y.outright.cumulative >= 0 && (y.year === m.outrightBreakeven) ? ' class="highlight"' : ''}>
            <td>${y.year}</td>
            <td>${fmt(y.generation)}</td>
            <td>${fmt3(system.tariffRate)}</td>
            <td>${fmt(y.grossRevenue)}</td>
            <td>${fmt(y.grossRevenue / 12)}</td>
            <td>${y.caTaxSaving > 0 ? fmt(y.caTaxSaving) : '—'}</td>
            <td>${y.itaTaxSaving > 0 ? fmt(y.itaTaxSaving) : '—'}</td>
            <td>${fmt(y.outright.cumulative)}</td>
        </tr>`).join('\n');

    const grossRevenue20yr = (m.years || []).reduce((s, y) => s + y.grossRevenue, 0);

    // Comparison summary page title / subtitle — adapts to which financing
    // options the salesperson ticked. e.g. SARE off → "Outright vs Loan".
    const fopts = financing || {};
    const titleParts = [];
    const subtitleParts = [];
    if (fopts.includeSARE !== false) { titleParts.push('SARE'); subtitleParts.push('Tenant'); }
    if (fopts.includeOutright !== false) { titleParts.push('Outright'); subtitleParts.push('Owner'); }
    if (fopts.includeLoan !== false) { titleParts.push('Loan'); subtitleParts.push('Cashflow-Conscious'); }
    const comparisonTitle = titleParts.length >= 2 ? titleParts.join(' vs ') : (titleParts[0] || 'Financing Comparison');
    const comparisonSubtitle = subtitleParts.length ? `Recommended for ${subtitleParts.join(' · ')}` : '';

    return {
        // Customer
        customer_name: customer.name || '—',
        customer_phone: customer.phone || '',
        customer_email: customer.email || '',
        customer_address: customer.address || '',
        // System
        system_size_kwp: kwpLabel,
        // System Capacity card: TOTAL kWp on top + per-meter breakdown below (matches the
        // savings cards). Breakdown derived from the "a+b+c" combo label; single → no split.
        system_kwp_total: String(+((Number(system.kwp) || 0).toFixed(2))),
        kwp_split: (kwpLabelOverride && String(kwpLabelOverride).includes('+'))
            ? `<div class="sub" style="font-size:8pt;color:#6B6B6B;font-weight:600;margin-top:1.5mm;">${String(kwpLabelOverride).split('+').map(s => s.trim()).join(' + ')} kWp</div>`
            : '',
        panel_count: panelCount,
        inverter_spec: inverterSpec,
        inverter_brand_label: inverterBrandLabel,
        inverter_kw_only: `${inverterKw} kW`,
        panel_watt: String(panelWatt),
        tariff_rate: fmt3(system.tariffRate),
        tariff_b_rate: fmt3(system.tariffRate * 1.004),
        // The HEADLINE figure is what the customer actually pays before SST — add-ons
        // included. Showing the add-on-free base here made the big number read as the
        // price while the real one sat RM3,000 higher in a sub-line. The add-on is still
        // broken out underneath as "incl. Add-on RM x" so the composition stays visible.
        // Also feeds the comparison table's "Upfront capital required", which must be the
        // full amount for the same reason. (user 2026-08-26)
        system_price: fmt(subtotal),
        system_price_base: fmt(system.systemCost),   // add-on-free base, if ever needed
        loan_principal_display: fmt(financing.loanPrincipal || system.systemCost),
        deposit: fmt(depositAmount),
        // Add-on + SST breakdown
        addon_amount: fmt(addOnAmount),
        sst_amount: fmt2(sstAmount),
        grand_total_price: fmt2(grandTotalPrice),
        // Site irradiance (Solar Site Data page)
        site_state: stateInfo.name,
        site_ghi: stateInfo.ghi.toFixed(1),
        site_psh: sitePsh.toFixed(1),
        performance_ratio: (PERFORMANCE_RATIO * 100).toFixed(2),
        // Site photo grid (omitted if empty)
        site_photos_html: photosHtml,
        site_photos_count: String(photoList.length),
        // Site design / scheme recommendation
        max_demand_kw: md > 0 ? String(md) : '—',
        recommended_scheme: schemeName,
        scheme_blurb: schemeBlurb,
        scheme_color: schemeColor,
        roof_panel_count: String(panelCount),
        roof_kwp: String(kwpLabel),
        // Battery (optional — empty strings when no battery)
        battery_kwh: batKwh > 0 ? String(batKwh) : '',
        battery_bom_row: batteryBomRow,
        battery_cover_frag: batteryCoverFrag,
        // Catalogue add-on BoM rows (empty string when no add-ons).
        addon_bom_rows: addonBomRows,
        // 90% generation guarantee note (projection-table footnotes; ≥54 kWp only).
        generation_guarantee_note: cscGenerationGuaranteeNote(lang, Number(system.kwp) || 0),
        // 10-yr battery warranty card per language (empty string when no battery).
        battery_warranty_card_en: batteryWarrantyCard('years', 'Product Warranty', 'Battery'),
        battery_warranty_card_cn: batteryWarrantyCard('年', '产品 保固', '电池'),
        battery_warranty_card_my: batteryWarrantyCard('tahun', 'Produk Waranti', 'Bateri'),
        om_annual: fmt(system.omAnnual),
        // Included-FREE O&M + cleaning line on the Product Warranties page, echoing the
        // CSC "Operation Warranty" dropdown (value like "3 Years (One-time Cleaning)").
        // This is the CURRENT free inclusion — distinct from the FUTURE O&M charges
        // (om_annual/om_period) and the paid Basic/Premium packages. (user 2026-08-02)
        ...(() => {
            const yrs = warrantyYears(system.operationWarranty);
            /* The cleaning is printed only when the chosen warranty HAS one. It used to be
               appended unconditionally, so a system under the 30 kWp threshold - whose
               dropdown reads plainly "1 Year" - was handed a proposal promising a free
               clean that is not part of the product.
               (user 2026-10-26: "the quotation show cleaning if got 1 time cleaning") */
            const clean = warrantyHasCleaning(system.operationWarranty);
            return {
                free_om_line_en: `${yrs} Year${yrs > 1 ? 's' : ''} Operation & Maintenance${clean ? ' · One-time Cleaning' : ''}`,
                free_om_line_cn: `${yrs} 年营运维护${clean ? ' · 一次清洗' : ''}`,
                free_om_line_my: `Operasi & Penyelenggaraan ${yrs} Tahun${clean ? ' · Pembersihan Sekali' : ''}`,
            };
        })(),
        // O&M interval: systems < 96 kWp are serviced once every 3 years (per system).
        // Multi-meter passes system.maintenancePeriodYears explicitly (per-meter rule).
        om_period: ((Number(system.maintenancePeriodYears) || (Number(system.kwp) < 96 ? 3 : 1)) > 1)
            ? `${Number(system.maintenancePeriodYears) || 3} years` : 'yr',
        // SARE block
        sare_system_price: fmt(system.systemCost),
        sare_tariff: fmt3(m.sareTariff || 0),
        sare_saving_per_kwh: fmt3((system.tariffRate || 0) - (m.sareTariff || 0)),
        // sare_monthly_saving / sare_yearly_saving now emit the NETT figure (2026-08-20).
        // They previously emitted annualCF, which assumes 100% self-consumption and so
        // overstates the saving by the whole exported surplus — on a 6-day site at ~69%
        // self-use that was RM1,073/mo printed against a true RM401/mo. The CN and MY
        // templates only ever referenced these two keys, so they had no way to show the
        // correct number. The _net_ keys below are kept as aliases so the EN template,
        // which already used them, keeps working.
        sare_monthly_saving: fmt((m.years?.[0]?.sare.annualNetCF != null ? m.years[0].sare.annualNetCF : (m.years?.[0]?.sare.annualCF || 0)) / 12),
        sare_yearly_saving: fmt(m.years?.[0]?.sare.annualNetCF != null ? m.years[0].sare.annualNetCF : (m.years?.[0]?.sare.annualCF || 0)),
        // NETT SARE saving — self-use at full tariff, exported surplus only at SMP (RM0.20).
        sare_monthly_net_saving: fmt((m.years?.[0]?.sare.annualNetCF != null ? m.years[0].sare.annualNetCF : (m.years?.[0]?.sare.annualCF || 0)) / 12),
        sare_yearly_net_saving: fmt(m.years?.[0]?.sare.annualNetCF != null ? m.years[0].sare.annualNetCF : (m.years?.[0]?.sare.annualCF || 0)),
        sare_20yr_net_saving: fmt((m.years || []).reduce((s, y) => s + (y.sare?.annualNetCF != null ? y.sare.annualNetCF : (y.sare?.annualCF || 0)), 0)),
        // Lowest-to-highest NETT monthly saving across the term. The SARE saving is not a
        // single number: the TNB tariff escalates ~8% every 3 years while the SARE rate is
        // fixed, so year 20 can be roughly double year 1. The headline quotes the range;
        // the comparison table keeps its explicit "Year 1" figure. (user 2026-08-21)
        // YEAR 1's Min ~ Max — the same two numbers as row 1 of the projection table
        // (user 2026-08-23). This used to span the whole term (lowest nett in any year to
        // highest nett in any year), which read as a year-1 figure but wasn't, and no
        // longer lines up now that the table itself shows Min/Max per year.
        //   Min = NETT (surplus exported at the RM0.20 SMP rate)
        //   Max = gross (every kWh self-consumed at the full TNB tariff)
        sare_monthly_saving_range: (() => {
            const y1 = m.years?.[0]?.sare;
            if (!y1) return fmt(0);
            const lo = Math.round((y1.annualNetCF != null ? y1.annualNetCF : y1.annualCF) / 12);
            const hi = Math.round((y1.annualCF || 0) / 12);
            return hi > lo ? `${fmt(lo)} ~ ${fmt(hi)}` : fmt(lo);
        })(),
        // Year 1 Min ~ Max as an ANNUAL figure. The summary now leads with the yearly
        // saving instead of the monthly one (user 2026-08-28), and dropping the monthly
        // card would otherwise have taken the Min~Max range with it — the single
        // sare_yearly_saving above is only the NETT (min) end.
        sare_yearly_saving_range: (() => {
            const y1 = m.years?.[0]?.sare;
            if (!y1) return fmt(0);
            const lo = Math.round(y1.annualNetCF != null ? y1.annualNetCF : y1.annualCF);
            const hi = Math.round(y1.annualCF || 0);
            return hi > lo ? `${fmt(lo)} ~ ${fmt(hi)}` : fmt(lo);
        })(),
        sare_20yr_saving: fmt(m.total20yrSARE || 0),
        sare_projection_rows: sareRows,

        // ── SARE 10-year block ──
        // Mirrors every sare_* key above, reading the sare10 per-year block and the
        // +8-sen lease rate. The saving range walks years 1-10 only, so it never quotes
        // a year-20 figure on a 10-year lease. (user 2026-08-23)
        sare10_term_years: '10',
        sare10_system_price: fmt(system.systemCost),
        sare10_tariff: fmt3(m.sareTariff10 || 0),
        sare10_saving_per_kwh: fmt3((system.tariffRate || 0) - (m.sareTariff10 || 0)),
        sare10_monthly_saving: fmt((m.years?.[0]?.sare10?.annualNetCF ?? m.years?.[0]?.sare10?.annualCF ?? 0) / 12),
        sare10_yearly_saving: fmt(m.years?.[0]?.sare10?.annualNetCF ?? m.years?.[0]?.sare10?.annualCF ?? 0),
        sare10_monthly_net_saving: fmt((m.years?.[0]?.sare10?.annualNetCF ?? m.years?.[0]?.sare10?.annualCF ?? 0) / 12),
        sare10_yearly_net_saving: fmt(m.years?.[0]?.sare10?.annualNetCF ?? m.years?.[0]?.sare10?.annualCF ?? 0),
        // Year 1 Min ~ Max, mirroring sare_monthly_saving_range above.
        sare10_monthly_saving_range: (() => {
            const y1 = m.years?.[0]?.sare10;
            if (!y1) return fmt(0);
            const lo = Math.round((y1.annualNetCF != null ? y1.annualNetCF : y1.annualCF) / 12);
            const hi = Math.round((y1.annualCF || 0) / 12);
            return hi > lo ? `${fmt(lo)} ~ ${fmt(hi)}` : fmt(lo);
        })(),
        // Annual Min ~ Max for the 10-year lease, mirroring sare_yearly_saving_range.
        sare10_yearly_saving_range: (() => {
            const y1 = m.years?.[0]?.sare10;
            if (!y1) return fmt(0);
            const lo = Math.round(y1.annualNetCF != null ? y1.annualNetCF : y1.annualCF);
            const hi = Math.round(y1.annualCF || 0);
            return hi > lo ? `${fmt(lo)} ~ ${fmt(hi)}` : fmt(lo);
        })(),
        sare10_10yr_saving: fmt(m.total10yrSARE || 0),
        // Years 11-20, when the lease has ended and the whole tariff is saved, plus the
        // 20-year grand total. Kept as their own tokens so the existing 10-year headline
        // keeps meaning the lease and nothing else.
        sare10_owned_saving: fmt(sare10OwnedTotal),
        sare10_20yr_saving: fmt(sare10LeaseTotal + sare10OwnedTotal),
        sare10_projection_rows: sare10Rows,
        // ── The year, month by month ──
        year_bill_chart: yearBillChart,
        year_bill_rows: yearBillRows,
        year_bill_months_given: yb ? String(yb.count) : '0',
        year_bill_cycle_note: yb
            ? (yb.annualised
                ? (12 % yb.count === 0
                    ? `${yb.count}-month cycle \u00d7 ${12 / yb.count}`
                    : `${yb.count} months, scaled to 12`)
                : 'a real 12 months')
            : '',
        year_bill_avg: yb ? fmt(yb.avgSaving) : '',
        year_bill_high: yb ? fmt(yb.maxSaving) : '',
        year_bill_low: yb ? fmt(yb.minSaving) : '',
        year_bill_annual: yb ? fmt(yb.annualSaving) : '',
        year_bill_payback: yb && yb.paybackYears ? yb.paybackYears.toFixed(1) : '',
        year_bill_total_bill: yb ? fmt(ybTotalBill) : '',
        year_bill_total_save: yb ? fmt(ybTotalSave) : '',
        year_bill_total_pay: yb ? fmt(Math.max(0, ybTotalBill - ybTotalSave)) : '',
        year_bill_total_pct: yb && ybTotalBill > 0 ? String(Math.round((ybTotalSave / ybTotalBill) * 100)) : '',
        // ── 15-year SARE ──
        sare15_term_years: '15',
        sare15_tariff: fmt3(m.sareTariff15 || 0),
        sare15_monthly_saving: fmt((m.years?.[0]?.sare15?.annualNetCF ?? m.years?.[0]?.sare15?.annualCF ?? 0) / 12),
        sare15_yearly_saving: fmt(m.years?.[0]?.sare15?.annualNetCF ?? m.years?.[0]?.sare15?.annualCF ?? 0),
        sare15_monthly_net_saving: fmt((m.years?.[0]?.sare15?.annualNetCF ?? m.years?.[0]?.sare15?.annualCF ?? 0) / 12),
        sare15_yearly_net_saving: fmt(m.years?.[0]?.sare15?.annualNetCF ?? m.years?.[0]?.sare15?.annualCF ?? 0),
        sare15_monthly_saving_range: (() => {
            const y1 = m.years?.[0]?.sare15;
            if (!y1) return fmt(0);
            const lo = Math.round((y1.annualNetCF != null ? y1.annualNetCF : y1.annualCF) / 12);
            const hi = Math.round((y1.annualCF || 0) / 12);
            return hi > lo ? `${fmt(lo)} ~ ${fmt(hi)}` : fmt(lo);
        })(),
        sare15_yearly_saving_range: (() => {
            const y1 = m.years?.[0]?.sare15;
            if (!y1) return fmt(0);
            const lo = Math.round(y1.annualNetCF != null ? y1.annualNetCF : y1.annualCF);
            const hi = Math.round(y1.annualCF || 0);
            return hi > lo ? `${fmt(lo)} ~ ${fmt(hi)}` : fmt(lo);
        })(),
        sare15_system_price: fmt(system.systemCost),
        sare15_saving_per_kwh: fmt3((system.tariffRate || 0) - (m.sareTariff15 || 0)),
        sare15_15yr_saving: fmt(m.total15yrSARE || 0),
        sare15_owned_saving: fmt(sare15OwnedTotal),
        sare15_20yr_saving: fmt(sare15LeaseTotal + sare15OwnedTotal),
        sare15_projection_rows: sare15Rows,

        // ── Comparison-summary SARE column ──
        // The comparison table shows ONE SARE column. When only the 10-year term is
        // selected it must not quote 20-year figures under "SARE (Tenant)" — that would
        // overstate the saving by the whole back half of the lease. Prefer the 20-year
        // when it is on (the default headline); otherwise fall back to the 10-year and
        // qualify the cell inline, since the row label ("20-year total saving") stays
        // correct for the Outright and Loan columns beside it.
        sare_compare_term: (fopts.includeSARE === false && fopts.includeSARE10 !== false) ? '10-year' : '20-year',
        sare_compare_monthly: (fopts.includeSARE === false && fopts.includeSARE10 !== false)
            ? fmt((m.years?.[0]?.sare10?.annualNetCF ?? m.years?.[0]?.sare10?.annualCF ?? 0) / 12)
            : fmt((m.years?.[0]?.sare.annualNetCF != null ? m.years[0].sare.annualNetCF : (m.years?.[0]?.sare.annualCF || 0)) / 12),
        sare_compare_total: (fopts.includeSARE === false && fopts.includeSARE10 !== false)
            ? `${fmt(m.total10yrSARE || 0)}<span class="sub">over the 10-year term</span>`
            : fmt(m.total20yrSARE || 0),
        // Outright block
        outright_projection_rows: outrightRows,
        total_20yr_outright: fmt(m.total20yrOutright || 0),
        // Loan block
        loan_projection_rows: loanRows,
        loan_principal: fmt(m.loanPrincipal || system.systemCost),
        loan_rate_pct: ((m.loanRate || 0) * 100).toFixed(2),
        loan_term_years: ((m.loanTermMonths || 0) / 12).toFixed(1),
        loan_monthly_pmt: fmt(m.loanMonthlyPmt || 0),
        loan_annual_pmt: fmt(m.loanAnnualPmt || 0),
        loan_total_paid: fmt((m.loanAnnualPmt || 0) * ((m.loanTermMonths || 0) / 12)),
        loan_total_interest: fmt(((m.loanAnnualPmt || 0) * ((m.loanTermMonths || 0) / 12)) - (m.loanPrincipal || system.systemCost)),
        loan_total_20yr: fmt(m.total20yrLoan || 0),
        // One set per selected term, keyed by months, filling the clones expandLoanTerms
        // made. Spread here so the single-loan vars above stay exactly as they were — a
        // proposal with no comparison never sees these.
        ...Object.fromEntries((m.loanPlans || []).flatMap(p => {
            const pfx = 'loan' + p.months + '_';
            const totalPaid = p.annualPmt * p.years;
            return [
                // The loan pages' own rows re-use the single-loan projection: the yearly
                // SAVINGS are the same whatever the tenure, only the repayment differs.
                [pfx + 'projection_rows', buildLoanRows(p.rows, p.breakeven)],
                [pfx + 'principal', fmt(p.principal)],
                [pfx + 'rate_pct', (p.rate * 100).toFixed(2)],
                [pfx + 'term_years', p.years.toFixed(p.years % 1 ? 1 : 0)],
                [pfx + 'monthly_pmt', fmt(p.monthlyPmt)],
                [pfx + 'annual_pmt', fmt(p.annualPmt)],
                [pfx + 'total_paid', fmt(totalPaid)],
                [pfx + 'total_interest', fmt(p.totalInterest)],
                [pfx + 'total_20yr', fmt(p.total20yr != null ? p.total20yr : (m.total20yrLoan || 0))],
                [pfx + 'breakeven_years', (() => {
                    const n = p.breakevenExact != null ? p.breakevenExact : m.loanBreakevenExact;
                    return (n == null) ? '—' : n.toFixed(1);
                })()],
                [pfx + 'breakeven_years_no_incentives', (() => {
                    const n = p.breakevenExactNoIncentives != null
                        ? p.breakevenExactNoIncentives
                        : (p.breakevenExact != null ? p.breakevenExact : m.loanBreakevenExact);
                    return (n == null) ? '—' : n.toFixed(1);
                })()],
            ];
        })),
        loan_breakeven_years: (() => {
            const n = m.loanBreakevenExact;
            return (n == null) ? '—' : n.toFixed(1);
        })(),
        loan_breakeven_years_no_incentives: (() => {
            const n = m.loanBreakevenExactNoIncentives;
            return (n == null) ? '—' : n.toFixed(1);
        })(),
        // Use Year-1 saving (more accurate for sales pitch; tariff escalator means
        // later years are larger, but Y1 is what the customer sees first).
        monthly_saving_avg: fmt(_summaryMo),
        yearly_saving_avg: fmt(_summaryMo * 12),
        // Multi-meter per-system breakdown lines ("RM a + b + c"); empty for single-meter.
        monthly_saving_split: (savingSplits && savingSplits.monthly) || '',
        yearly_saving_split: (savingSplits && savingSplits.yearly) || '',
        total20_saving_split: (savingSplits && savingSplits.total20) || '',
        // Payback shown as exact fractional years (e.g. 1.8) via linear
        // interpolation within the breakeven year. WITH CA + GITA and
        // WITHOUT CA + GITA both exposed for side-by-side comparison.
        payback_years: (() => {
            const n = m.outrightBreakevenExact;
            return (n == null) ? '—' : n.toFixed(1);
        })(),
        payback_years_no_incentives: (() => {
            const n = m.outrightBreakevenExactNoIncentives;
            return (n == null) ? '—' : n.toFixed(1);
        })(),
        irr_with_ca_pct: m.outrightIRR != null ? (m.outrightIRR * 100).toFixed(1) : '—',
        irr_no_ca_pct: m.outrightIRRWithoutCA != null ? (m.outrightIRRWithoutCA * 100).toFixed(1) : '—',
        lcoe: fmt3(m.lcoe || 0),
        gross_20yr_revenue: fmt(grossRevenue20yr),
        total_ca_claim: fmt(m.cumCATaxSaving || 0),
        total_gita_claim: fmt(m.itaCredit || 0),
        // Sales person
        sales_person_name: sales.name || '—',
        sales_person_title: sales.title || 'Solar Consultant',
        sales_person_email: sales.email || '',
        sales_person_whatsapp: sales.whatsapp || '',
        sales_person_photo: sales.photoUrl || '',
        sales_person_initial: (sales.name || 'A').trim().charAt(0).toUpperCase(),
        // Dates
        date_issued: dates.issued || todayDMY(),
        date_valid_until: dates.valid || plus30DMY(),
        // Financing comparison summary
        comparison_title: comparisonTitle,
        comparison_subtitle: comparisonSubtitle,
    };
}

/**
 * Repeat the LOAN pages once per selected term, so 3 / 5 / 7 years can be compared in one
 * proposal. (user 2026-10-15: "can we select also 3,5,7 years loan either one? or choose
 * any two or 3 aso can as comparison")
 *
 * Done on the RAW TEMPLATE, before substitution, and that is the whole point: the three
 * language templates (EN / CN / MY) keep ONE loan block each and are not touched. Hand-
 * duplicating three pages across three files would have been nine pages to keep in step
 * for every future wording change, and they would drift.
 *
 * Each clone gets its own `loan-term-<months>` class and its `{{loan_*}}` placeholders
 * renamed to `{{loanN_*}}`, which buildCscVarMap fills from math.loanPlans.
 *
 * `terms` empty, absent, or a single term identical to the existing one → the template is
 * returned UNCHANGED, so a proposal that does not ask for a comparison is byte-identical
 * to today's.
 *
 * STRING SPLICING, NOT DOM. The first cut of this parsed the whole document, and an HTML
 * parser FOSTER-PARENTS text that sits inside a table but outside a cell — every projection
 * body in the templates is a bare `<tbody>{{x_projection_rows}}</tbody>`, so Outright, SARE
 * and SARE-10 all had their rows thrown out of their tables and printed as a paragraph of
 * loose numbers. Parsing also rewrites `<head>` and whitespace elsewhere. Splicing the raw
 * string copies every character outside the loan pages across untouched, which is what the
 * rest of the deck is entitled to. (user 2026-10-16: "outright shdnt have any changes
 * right", "for proposal the rest no change right")
 */
const LOAN_TERM_WORD = { en: 'Years', cn: '年', my: 'Tahun' };

/** "3 Years" / "3 年" / "3 Tahun" - the label that titles each term's own section. */
function loanTermLabel(months, lang) {
    const y = months / 12;
    const n = y.toFixed(y % 1 ? 1 : 0);
    const word = LOAN_TERM_WORD[lang] || LOAN_TERM_WORD.en;
    return lang === 'cn' ? (n + word) : (n + ' ' + word);
}

export function expandLoanTerms(html, terms, lang) {
    const months = (terms || []).map(t => Math.round(Number(t && t.months) || 0)).filter(n => n > 0);
    if (months.length === 0) return html;

    const blocks = findLoanBlocks(html);
    if (blocks.length === 0) return html;

    // Each loan PAGE is cloned per term and the clones kept together, so the proposal reads
    // "3-year banner, 3-year detail, 3-year cashflow, then 5-year ..." rather than interleaving
    // three banners and then three detail pages.
    const sorted = [...new Set(months)].sort((a, b) => a - b);
    let expanded = '';
    for (const m of sorted) {
        for (const [s, e] of blocks) expanded += loanBlockForTerm(html.slice(s, e), m, lang);
    }

    // Splice: the originals come out, the full set goes in where the last one stood, and every
    // character outside those blocks is copied across untouched.
    let out = '';
    let prev = 0;
    blocks.forEach(([s, e], i) => {
        out += html.slice(prev, s);
        if (i === blocks.length - 1) out += expanded;
        prev = e;
    });
    return expandComparisonColumns(out + html.slice(prev), sorted, lang);
}

/**
 * The comparison summary (#p16) carries ONE "Loan-Financed" column. With terms to compare
 * it has to carry one per term, or the summary contradicts the pages it is summarising -
 * it would quote a single monthly, a single ROI and a single 20-year saving for three
 * different loans. (user 2026-10-16: "Can fix all the summary and table too")
 *
 * Every `col-loan` cell is repeated per term with its `{{loan_*}}` renamed, so the values
 * follow automatically; nothing else in the table is touched, and `postProcessCscProposal`
 * still drops them all together when Loan is unticked.
 */
export function expandComparisonColumns(html, months, lang) {
    if (!months || months.length === 0) return html;
    // Fixed layout only when the table actually grows; one term leaves it exactly as it was.
    if (months.length > 1) {
        html = html.replace('<table class="comparison-table">', '<table class="comparison-table multi-loan">');
    }
    return html.replace(/<(th|td) class="col-loan([^"]*)"([^>]*)>([\s\S]*?)<\/\1>/g,
        (full, tag, restClass, attrs, inner) => months.map((m) => {
            const label = loanTermLabel(m, lang);
            let body = inner.replace(/\{\{loan_/g, '{{loan' + m + '_');
            if (tag === 'th') {
                // The heading says WHICH loan this column is.
                const i = body.indexOf('<span');
                body = i >= 0
                    ? body.slice(0, i) + ' \u00b7 ' + label + body.slice(i)
                    : body + ' \u00b7 ' + label;
            }
            return '<' + tag + ' class="col-loan' + restClass + ' loan-term-' + m + '"'
                + attrs + '>' + body + '</' + tag + '>';
        }).join(''));
}

/** Byte offsets [start, end) of every `loan-section` page, found by walking the div tags. */
export function findLoanBlocks(html) {
    const blocks = [];
    const opener = /<div\b[^>]*\bclass="[^"]*\bloan-section\b[^"]*"[^>]*>/g;
    const anyDiv = /<!--[\s\S]*?-->|<(\/?)div\b[^>]*>/g;
    let m;
    while ((m = opener.exec(html))) {
        let depth = 0;
        let end = -1;
        anyDiv.lastIndex = m.index;
        let t;
        while ((t = anyDiv.exec(html))) {
            if (t[0].startsWith('<!--')) continue;
            depth += t[1] ? -1 : 1;
            if (depth === 0) { end = t.index + t[0].length; break; }
        }
        if (end < 0) break;              // unbalanced markup: leave the template alone
        blocks.push([m.index, end]);
        opener.lastIndex = end;
    }
    return blocks;
}

/** One loan page, rewritten for a single term. */
function loanBlockForTerm(block, m, lang) {
    const label = loanTermLabel(m, lang);
    return block
        // The page is tagged so postProcessCscProposal and the print CSS can still find it.
        .replace(/(<div\b[^>]*\bclass=")([^"]*\bloan-section\b[^"]*)(")/, '$1$2 loan-term-' + m + '$3')
        // Ids must stay unique or the anchors in the deck collide.
        .replace(/\bid="([^"]*)"/g, 'id="$1-t' + m + '"')
        .replace(/\{\{loan_/g, '{{loan' + m + '_')
        /* Each term's pages carry the term in their own titles, so three loan sections in one
           deck can be told apart while reading it. (user 2026-10-16: "for 3 years , 5 years ,
           7 years need to have title for each. 3years Loan Financed") */
        .replace(/(<div class="option-title">)/, '$1' + label.toUpperCase() + '<br>')
        .replace(/(<div class="page-eyebrow">)([^<]*)/, '$1$2 \u00b7 ' + label)
        .replace(/(<div class="brand-mark-text">[\s\S]*?<small>)([^<]*)/, '$1$2 \u00b7 ' + label);
}

export function cscSubstitute(html, map) {
    let out = html;
    for (const key of Object.keys(map)) {
        out = out.split(`{{${key}}}`).join(map[key] != null ? String(map[key]) : '');
    }
    return out;
}

// Drop unticked financing sections from the rendered HTML.
// Comparison summary page (#p16) adapts: columns drop one at a time as
// options are unticked; the whole page is removed only if fewer than 2
// financing options remain (no comparison is meaningful with 0 or 1).
/* ── BESS peak shaving pages (user 2026-09-25) ────────────────────────────────────────────
   Built in CODE, not in the three language templates: the content is entirely data (months of
   readings, kW shaved, payback) and a hand-maintained copy in EN/CN/MY would drift.
   Part A is the solar system; this is Part B when both are proposed, Part A when the battery
   stands alone — solar takes the ENERGY charge, the battery takes the DEMAND charge that solar
   cannot touch (on a real MV bill, ~64% of the money). */
const BESS_I18N = {
    en: { part: 'Part', tag: 'BESS', title: 'BATTERY ENERGY<br>STORAGE SYSTEM<small>Peak Shaving</small>', by: 'By Astern Technologies Sdn Bhd',
        eyebrow: 'Maximum demand — the charge solar cannot reach', heading: 'Peak Shaving with BESS',
        lead: 'Your bill charges capacity and network on the highest half-hour of demand inside 2pm–10pm on weekdays. Solar cannot promise to lower it — one cloudy half hour sets the month. A battery can, by discharging on command.',
        cap: 'Battery', power: 'Power', mdBefore: 'MD before', mdAfter: 'MD after', shaved: 'MD shaved', saving: 'Monthly saving', capex: 'Investment', payback: 'Payback',
        month: 'Month', tblBefore: 'MD before (kW)', tblAfter: 'MD after (kW)', tblShaved: 'Shaved (kW)', tblSaving: 'Saving (RM)',
        basis: (n, rate, credit) => `Measured from ${n} month${n === 1 ? '' : 's'} of your own 30-minute meter data · demand billed at RM ${rate}/kW per month · ${credit}% credited as a safety margin`,
        units: (u, kwh) => `${u} × ${kwh} kWh battery block${u === 1 ? '' : 's'}`,
        sizingEyebrow: 'How the battery was sized', sizingTitle: 'Sizing and the options considered',
        worstDay: 'Sizing day (heaviest discharge)', peak: 'Peak', hold: 'Held at', discharge: 'Energy discharged',
        option: 'Option', pbNone: 'No incentive', pbCA: 'With capital allowance', pbGita: 'With CA + GITA', yr: 'yr',
        chain: (kwh, inst) => `That day the battery must deliver <strong>${kwh} kWh</strong> inside the peak window. Allowing for depth of discharge (90%), discharge efficiency (95%) and capacity after ten years (78.51%), the installed capacity needed is <strong>${inst} kWh</strong>.`,
        sizingNote: 'Capital allowance writes the system off against tax over six claim years; GITA adds a further allowance on qualifying capital expenditure. Figures are estimates, not tax advice.',
        charts: { dayTitle: 'The highest demand day, held down', kw: 'kW', before: 'Recorded demand', after: 'With BESS', cap: 'Held at', shavedLegend: 'Shaved',
            mdTitle: 'Maximum demand by month — before and after', paybackTitle: 'Payback after capital allowance', years: 'Years',
            shavedTitle: 'Average MD shaved', cashTitle: '20-year cumulative cashflow', cashY: 'Cumulative RM', unitsAxis: 'Battery blocks',
            chartsEyebrow: 'The readings behind the proposal', chartsTitle: 'What your own meter data shows',
            chartsNote: 'Every figure on these charts is computed from the 30-minute interval data supplied for this site. Nothing is modelled or assumed.' },
    },
    cn: { part: '部分', tag: '储能', title: '电池储能系统<br>BESS<small>削峰</small>', by: 'Astern Technologies Sdn Bhd',
        eyebrow: '最高需量 — 太阳能无法处理的收费', heading: 'BESS 削峰',
        lead: '账单的容量费与网络费按工作日下午 2 点至 10 点之间最高的半小时需量计算。太阳能无法保证降低它——一段阴天就决定了整个月。电池可以，因为它随时可放电。',
        cap: '电池', power: '功率', mdBefore: '削峰前 MD', mdAfter: '削峰后 MD', shaved: '削减 MD', saving: '每月节省', capex: '投资', payback: '回本期',
        month: '月份', tblBefore: '削峰前 (kW)', tblAfter: '削峰后 (kW)', tblShaved: '削减 (kW)', tblSaving: '节省 (RM)',
        basis: (n, rate, credit) => `根据贵司 ${n} 个月的 30 分钟电表数据 · 需量费 RM ${rate}/kW 每月 · 以 ${credit}% 计算（安全余量）`,
        units: (u, kwh) => `${u} × ${kwh} kWh 电池模块`,
        sizingEyebrow: '电池容量如何计算', sizingTitle: '容量计算与方案比较',
        worstDay: '选型日（放电最多）', peak: '峰值', hold: '压至', discharge: '放电电量',
        option: '方案', pbNone: '无奖掩', pbCA: '含资本津贴 (CA)', pbGita: '含 CA + GITA', yr: '年',
        chain: (kwh, inst) => `当天电池需在高峰时段放出 <strong>${kwh} kWh</strong>。计入放电深度 (90%)、放电效率 (95%) 与十年后容量 (78.51%)，所需装机容量为 <strong>${inst} kWh</strong>。`,
        sizingNote: '资本津贴分六年抵扣税务；GITA 另有合格资本开支津贴。以上为估算，非税务意见。',
        charts: { dayTitle: '最高需量日的削峰情况', kw: 'kW', before: '实际需量', after: '加装 BESS 后', cap: '压至', shavedLegend: '削减',
            mdTitle: '各月最高需量 — 削峰前后', paybackTitle: '扣除资本津贴后的回本期', years: '年',
            shavedTitle: '平均削减 MD', cashTitle: '20 年累计现金流', cashY: '累计 RM', unitsAxis: '电池组数',
            chartsEyebrow: '方案背后的实测数据', chartsTitle: '贵司电表数据显示的结果',
            chartsNote: '以下图表全部由本案场提供的 30 分钟区间数据计算得出，并非模型推算。' },
    },
    my: { part: 'Bahagian', tag: 'BESS', title: 'SISTEM SIMPANAN<br>TENAGA BATERI<small>Peak Shaving</small>', by: 'Oleh Astern Technologies Sdn Bhd',
        eyebrow: 'Permintaan maksimum — caj yang solar tidak dapat kurangkan', heading: 'Peak Shaving dengan BESS',
        lead: 'Caj kapasiti dan rangkaian dikira pada setengah jam permintaan tertinggi antara 2pm–10pm pada hari bekerja. Solar tidak boleh menjamin ia turun — satu setengah jam mendung menentukan bulan itu. Bateri boleh.',
        cap: 'Bateri', power: 'Kuasa', mdBefore: 'MD sebelum', mdAfter: 'MD selepas', shaved: 'MD dikurangkan', saving: 'Penjimatan sebulan', capex: 'Pelaburan', payback: 'Bayar balik',
        month: 'Bulan', tblBefore: 'MD sebelum (kW)', tblAfter: 'MD selepas (kW)', tblShaved: 'Dikurangkan (kW)', tblSaving: 'Penjimatan (RM)',
        basis: (n, rate, credit) => `Daripada ${n} bulan data meter 30-minit anda · permintaan dicaj RM ${rate}/kW sebulan · ${credit}% dikreditkan`,
        units: (u, kwh) => `${u} × ${kwh} kWh blok bateri`,
        sizingEyebrow: 'Bagaimana saiz bateri ditentukan', sizingTitle: 'Penentuan saiz dan pilihan',
        worstDay: 'Hari saiz (nyahcas terbanyak)', peak: 'Puncak', hold: 'Ditahan pada', discharge: 'Tenaga dinyahcas',
        option: 'Pilihan', pbNone: 'Tanpa insentif', pbCA: 'Dengan elaun modal', pbGita: 'Dengan CA + GITA', yr: 'thn',
        chain: (kwh, inst) => `Pada hari itu bateri perlu membekal <strong>${kwh} kWh</strong> dalam tempoh puncak. Mengambil kira DoD (90%), kecekapan nyahcas (95%) dan kapasiti selepas sepuluh tahun (78.51%), kapasiti terpasang yang diperlukan ialah <strong>${inst} kWh</strong>.`,
        sizingNote: 'Elaun modal melunaskan kos terhadap cukai dalam enam tahun tuntutan; GITA menambah elaun ke atas perbelanjaan modal yang layak. Angka adalah anggaran, bukan nasihat cukai.',
        charts: { dayTitle: 'Hari permintaan tertinggi, ditahan turun', kw: 'kW', before: 'Permintaan direkod', after: 'Dengan BESS', cap: 'Ditahan pada', shavedLegend: 'Dikurangkan',
            mdTitle: 'Permintaan maksimum mengikut bulan — sebelum dan selepas', paybackTitle: 'Bayar balik selepas elaun modal', years: 'Tahun',
            shavedTitle: 'Purata MD dikurangkan', cashTitle: 'Aliran tunai terkumpul 20 tahun', cashY: 'RM terkumpul', unitsAxis: 'Blok bateri',
            chartsEyebrow: 'Data sebenar di sebalik cadangan ini', chartsTitle: 'Apa yang data meter anda tunjukkan',
            chartsNote: 'Setiap angka pada carta ini dikira daripada data selang 30 minit tapak ini. Tiada anggaran atau model.' },
    },
};

/**
 * Two proposal pages for the battery: an option banner and the numbers.
 * `bess` = the chosen result from utils/cscBessPeakShaving (units, monthly[], capex, …).
 */
// Part A cover - the solar half of a two-part proposal. Only printed when a BESS Part B
// follows: on a solar-only proposal there is nothing to be Part A OF, and an extra divider
// page would just push the page count up. Same markup as the BESS banner so both covers
// read as a pair. (user 2026-09-26)
const PART_A_I18N = {
    en: { part: 'Part', title: 'SOLAR PV<br>SYSTEM<small>Energy Saving</small>', by: 'By Astern Technologies Sdn Bhd' },
    cn: { part: 'Part', title: '太阳能光伏<br>系统<small>节省电费</small>', by: 'Astern Technologies Sdn Bhd 呈献' },
    my: { part: 'Bahagian', title: 'SISTEM<br>SOLAR PV<small>Penjimatan Tenaga</small>', by: 'Oleh Astern Technologies Sdn Bhd' },
};
export function buildPartACoverHtml({ lang = 'en' } = {}) {
    const t = PART_A_I18N[lang] || PART_A_I18N.en;
    return `
<div class="page option-banner part-a-cover" id="p-part-a">
  <div class="option-tag">${t.part} A</div>
  <div class="option-title">${t.title}</div>
  <div class="option-bottom">${t.by}</div>
</div>`;
}

export function buildBessSectionHtml(bess, {
    lang = 'en', partLetter = 'B', customerName = '', dateIssued = '',
    unitKwh = 261, demandRate = 98.61, creditPct = 90, brandLabel = 'GoodWe',
    ladder = [], qualifyYears = 4,
} = {}) {
    if (!bess || !bess.monthly?.length) return '';
    const t = BESS_I18N[lang] || BESS_I18N.en;
    const rm = (n) => Math.round(n).toLocaleString('en-US');
    const mdBefore = Math.max(...bess.monthly.map(m => m.baseline));
    const mdAfter = Math.max(...bess.monthly.map(m => m.after));
    const rows = bess.monthly.map(m => `<tr><td>${m.month}</td><td>${rm(m.baseline)}</td><td>${rm(m.after)}</td><td>${rm(m.shaved)}</td><td>RM ${rm(m.saving)}</td></tr>`).join('');
    const footer = `<div class="page-footer"><span class="company">Astern Technologies Sdn Bhd</span><span class="ref">${customerName} · ${dateIssued}</span></div>`;
    // Charts are PNG data URIs drawn on an offscreen canvas (see utils/cscBessCharts) so the
    // html2canvas -> jsPDF pass renders them as plain images. A chart that fails to draw
    // returns '' and its block is simply left out. (user 2026-09-26)
    const c = t.charts || {};
    const imgs = {
        /* The HIGHEST-DEMAND day, not the heaviest-energy one. The chart is the customer's
           picture of what they are buying, so it has to be the day that sets their bill -
           and it is the day the Astern analyzer puts on its Top-3 #1 tab, so the deck and
           that tool now show the same shape. The sizing page below still reports
           bess.worstDay, which is a different day and a different question.
           (user 2026-10-26: "how to match my graph?") */
        day: chartDayProfile(bess.peakDaySeries || bess.worstDaySeries, {
            capKw: (bess.peakDay || bess.worstDay)?.capKw, title: c.dayTitle, yLabel: c.kw,
            labelBefore: c.before, labelAfter: c.after, labelCap: c.cap, labelShaved: c.shavedLegend,
        }),
        md: chartMonthlyMd(bess.monthly, { title: c.mdTitle, yLabel: c.kw, labelBefore: t.mdBefore, labelAfter: t.mdAfter }),
        payback: chartPaybackByUnit(ladder, { qualifyYears, chosenUnits: bess.units, title: c.paybackTitle, yLabel: c.years }),
        shaved: chartShavedByUnit(ladder, { chosenUnits: bess.units, title: c.shavedTitle, yLabel: c.kw }),
        cash: chartCashflow(bess.cashflow, { title: c.cashTitle, yLabel: c.cashY, labelYear: t.yr }),
    };
    const fig = (src, width) => (src ? `<div style="text-align:center;margin-top:10px;"><img src="${src}" style="width:${width};height:auto;display:block;margin:0 auto;"></div>` : '');
    return `
<div class="page option-banner bess-section" id="p-bess-banner">
  <div class="option-tag">${t.part} ${partLetter}</div>
  <div class="option-title">${t.title}</div>
  <div class="option-bottom">${t.by}</div>
</div>
<div class="page bess-section" id="p-bess-detail">
  <div class="brand-header"><div class="brand-mark"><div class="brand-mark-text">Astern Technologies<small>${t.tag}</small></div></div></div>
  <div class="page-eyebrow">${t.eyebrow}</div>
  <h1 class="page-title">${t.heading}<small>${t.units(bess.units, unitKwh)} · ${brandLabel}</small></h1>
  <div class="page-divider"></div>
  <p style="font-size:11pt;line-height:1.6;margin:0 0 14px;">${t.lead}</p>
  <div class="summary-grid">
    <div class="summary-card"><div class="label">${t.cap}</div><div class="value">${rm(bess.energyKwh)}<small>kWh</small></div></div>
    <div class="summary-card"><div class="label">${t.power}</div><div class="value">${rm(bess.powerKw)}<small>kW</small></div></div>
    <div class="summary-card"><div class="label">${t.mdBefore}</div><div class="value">${rm(mdBefore)}<small>kW</small></div></div>
    <div class="summary-card"><div class="label">${t.mdAfter}</div><div class="value">${rm(mdAfter)}<small>kW</small></div></div>
    <div class="summary-card"><div class="label">${t.saving}</div><div class="value">RM ${rm(bess.monthlyAvgSaving)}</div></div>
    <div class="summary-card"><div class="label">${t.payback}</div><div class="value">${bess.paybackYears.toFixed(1)}<small>${lang === 'cn' ? '年' : 'yr'}</small></div></div>
  </div>
  <table class="proj-table" style="margin-top:14px;">
    <thead><tr><th>${t.month}</th><th>${t.tblBefore}</th><th>${t.tblAfter}</th><th>${t.tblShaved}</th><th>${t.tblSaving}</th></tr></thead>
    <tbody>${rows}</tbody>
  </table>
  ${fig(imgs.md, '100%')}
  <p style="font-size:9pt;color:#64748b;margin-top:10px;">${t.basis(bess.monthly.length, demandRate.toFixed(2), creditPct)} · ${t.capex}: RM ${rm(bess.capex)}</p>
  ${footer}
</div>
${buildBessChartsPage({ t, c, imgs, fig, footer })}
${buildBessSizingPage(bess, { t, rm, footer, unitKwh, ladder, qualifyYears })}`;
}

/**
 * The charts page. The tables already carry the numbers; these say the same thing in a form a
 * plant manager reads in one glance — the day being flattened, the months in a row, the point
 * where another block stops earning, and the year the money comes back. Drawn from the site's
 * own interval data, never modelled. (user 2026-09-26)
 */
function buildBessChartsPage({ t, c, imgs, fig, footer }) {
    if (!imgs.day && !imgs.cash && !imgs.payback) return '';
    const pair = (a, b) => (a || b
        ? `<div style="display:flex;gap:8px;margin-top:10px;align-items:flex-start;">
             <div style="flex:1;min-width:0;">${a ? `<img src="${a}" style="width:100%;height:auto;display:block;">` : ''}</div>
             <div style="flex:1;min-width:0;">${b ? `<img src="${b}" style="width:100%;height:auto;display:block;">` : ''}</div>
           </div>`
        : '');
    return `
<div class="page bess-section" id="p-bess-charts">
  <div class="brand-header"><div class="brand-mark"><div class="brand-mark-text">Astern Technologies<small>${t.tag}</small></div></div></div>
  <div class="page-eyebrow">${c.chartsEyebrow || ''}</div>
  <h1 class="page-title">${c.chartsTitle || ''}</h1>
  <div class="page-divider"></div>
  ${fig(imgs.day, '100%')}
  ${pair(imgs.shaved, imgs.payback)}
  ${fig(imgs.cash, '100%')}
  <p style="font-size:9pt;color:#64748b;margin-top:10px;">${c.chartsNote || ''}</p>
  ${footer}
</div>`;
}

/**
 * Third BESS page: HOW the size was reached and what the alternatives cost — the working from
 * Astern's "Designing BESS System for Peak Shaving" (REV-0): worst demand day → energy the pack
 * must discharge → the DoD / efficiency / end-of-life chain → installed kWh; then units against
 * saving and payback, and the three payback lines (none / CA / CA + GITA). Rendered from OUR
 * data, not copied from that deck. (user 2026-09-25)
 */
function buildBessSizingPage(bess, { t, rm, footer, unitKwh, ladder = [], qualifyYears = 4 }) {
    const w = bess.worstDay;
    if (!w && !ladder.length) return '';
    const rows = ladder.map(r => `<tr${r.units === bess.units ? ' style="font-weight:700;background:#F1F5F9;"' : ''}>
        <td>${r.units} × ${unitKwh} kWh</td>
        <td>${rm(r.avgShavedKw)}</td>
        <td>RM ${rm(r.monthlyAvgSaving)}</td>
        <td>RM ${rm(r.capex)}</td>
        <td>${r.paybackYears.toFixed(2)}</td>
        <td>${r.paybackWithCA.toFixed(2)}</td>
        <td style="color:${r.qualifies ? '#15803d' : '#b91c1c'};">${r.qualifies ? '✓' : '✗'}</td>
      </tr>`).join('');
    return `
<div class="page bess-section" id="p-bess-sizing">
  <div class="brand-header"><div class="brand-mark"><div class="brand-mark-text">Astern Technologies<small>${t.tag}</small></div></div></div>
  <div class="page-eyebrow">${t.sizingEyebrow}</div>
  <h1 class="page-title">${t.sizingTitle}</h1>
  <div class="page-divider"></div>
  ${w ? `<div class="summary-grid">
    <div class="summary-card"><div class="label">${t.worstDay}</div><div class="value" style="font-size:14pt;">${w.dayKey}</div></div>
    <div class="summary-card"><div class="label">${t.peak}</div><div class="value">${rm(w.peakKw)}<small>kW</small></div></div>
    <div class="summary-card"><div class="label">${t.hold}</div><div class="value">${rm(w.capKw)}<small>kW</small></div></div>
    <div class="summary-card"><div class="label">${t.discharge}</div><div class="value">${rm(w.dischargeKwh)}<small>kWh</small></div></div>
  </div>
  <p style="font-size:10pt;line-height:1.6;margin:12px 0 0;">${t.chain(rm(w.dischargeKwh), rm(bess.requiredInstalledKwh))}</p>` : ''}
  ${rows ? `<table class="proj-table" style="margin-top:14px;">
    <thead><tr><th>${t.option}</th><th>${t.tblShaved}</th><th>${t.tblSaving}</th><th>${t.capex}</th><th>${t.pbNone}</th><th>${t.pbCA}</th><th>${qualifyYears}${t.yr}</th></tr></thead>
    <tbody>${rows}</tbody>
  </table>` : ''}
  <div class="summary-grid" style="margin-top:14px;">
    <div class="summary-card"><div class="label">${t.pbNone}</div><div class="value">${bess.paybackYears.toFixed(2)}<small>${t.yr}</small></div></div>
    <div class="summary-card"><div class="label">${t.pbCA}</div><div class="value">${bess.paybackWithCA.toFixed(2)}<small>${t.yr}</small></div></div>
    <div class="summary-card"><div class="label">${t.pbGita}</div><div class="value">${bess.paybackWithCAGita.toFixed(2)}<small>${t.yr}</small></div></div>
  </div>
  <p style="font-size:9pt;color:#64748b;margin-top:10px;">${t.sizingNote}</p>
  ${footer}
</div>`;
}

export function postProcessCscProposal(html, { includeSARE, includeSARE10, includeSARE15, includeOutright, includeLoan, includePhotos, hasYearBills = false, companyProfileOnly, bessHtml, bessLang = 'en' } = {}) {
    // Company Profile = the proposal WITHOUT the customer/pricing pages. Drop every option
    // section (SARE / Outright / Loan), the comparison summary, the site-design/photos page,
    // and the per-system Bill-of-Material (#p18) — leaving only the generic brand/marketing
    // pages (who-we-are, track record, case studies, partners, Tier-1, O&M, monitoring,
    // timeline, giveback). (user 2026-08-05)
    if (companyProfileOnly) {
        let cdoc;
        try { cdoc = new DOMParser().parseFromString(html, 'text/html'); }
        catch { return html; }
        // Keep the cover page, but drop the proposal-specific box on it (Prepared For /
        // Site Address / System Size) — a company profile has no customer or system yet.
        // (user 2026-08-06)
        cdoc.querySelectorAll('.cover-card, .sare-section, .outright-section, .loan-section, .comparison-summary-section, .site-photos-section, #p18')
            .forEach((el) => el.remove());
        return '<!doctype html>\n' + cdoc.documentElement.outerHTML;
    }
    // SARE counts ONCE toward the comparison summary no matter how many terms are shown —
    // 10-year and 20-year are two flavours of the same option, not two options.
    const anySare = !!(includeSARE || includeSARE10 || includeSARE15);
    const selectedCount = (anySare ? 1 : 0) + (includeOutright ? 1 : 0) + (includeLoan ? 1 : 0);
    // Nothing to drop AND nothing to add — hand the template back untouched. The `!bessHtml`
    // matters: this shortcut predates Part B, and without it a proposal with every option
    // ticked and site photos attached returned here before the BESS pages were inserted, so
    // Part A's cover and the whole of Part B silently vanished from exactly the fullest deck.
    // (user 2026-10-05: BESS card on screen, no Part B in the PDF)
    if (includeSARE && includeSARE10 && includeSARE15 && includeOutright && includeLoan && includePhotos && hasYearBills && !bessHtml) return html;
    let doc;
    try { doc = new DOMParser().parseFromString(html, 'text/html'); }
    catch { return html; }
    if (!anySare) {
        // Drops the shared banner + "how SARE works" pages along with both term sections,
        // since every SARE page carries .sare-section.
        doc.querySelectorAll('.sare-section').forEach((el) => el.remove());
        doc.querySelectorAll('.col-sare').forEach((el) => el.remove());
    } else {
        // Keep the shared pages; drop only the term(s) not selected.
        if (!includeSARE) doc.querySelectorAll('.sare20-section').forEach((el) => el.remove());
        if (!includeSARE10) doc.querySelectorAll('.sare10-section').forEach((el) => el.remove());
        if (!includeSARE15) doc.querySelectorAll('.sare15-section').forEach((el) => el.remove());
    }
    /* No bills, no page. Printing it with one month repeated twelve times would claim a
       precision the agent never gave us. */
    if (!hasYearBills) {
        doc.querySelectorAll('.year-bills-section').forEach((el) => el.remove());
    }
    if (!includeOutright) {
        doc.querySelectorAll('.outright-section').forEach((el) => el.remove());
        doc.querySelectorAll('.col-outright').forEach((el) => el.remove());
    }
    if (!includeLoan) {
        doc.querySelectorAll('.loan-section').forEach((el) => el.remove());
        doc.querySelectorAll('.col-loan').forEach((el) => el.remove());
    }
    if (selectedCount < 2) {
        doc.querySelectorAll('.comparison-summary-section').forEach((el) => el.remove());
    }
    if (!includePhotos) {
        doc.querySelectorAll('.site-photos-section').forEach((el) => el.remove());
    }
    // Two-part proposal: a Part A cover opens the solar half (just before the first option
    // banner - Option A / SARE), and Part B follows the System Components & Warranties page,
    // which closes the solar story. Before, Part B sat at the very end after the closing
    // matter, and Part A had no cover at all. (user 2026-09-26)
    if (bessHtml) {
        const holder = doc.createElement('div');
        holder.innerHTML = bessHtml;
        const pages = [...holder.children];
        // Part A cover - only when the BESS really is a second part. A BESS-only proposal
        // labels ITSELF Part A, so it must not get a solar cover in front of it.
        const bessIsPartB = /option-tag">[^<]*\bB\s*</.test(bessHtml);
        const firstBanner = doc.querySelector('.page.option-banner');
        if (bessIsPartB && firstBanner) {
            const aHolder = doc.createElement('div');
            aHolder.innerHTML = buildPartACoverHtml({ lang: bessLang });
            [...aHolder.children].forEach((pg) => firstBanner.parentNode.insertBefore(pg, firstBanner));
        }
        // Part B goes straight after the Bill of Material page; falls back to the old spot.
        const bom = doc.querySelector('#p18');
        const back = doc.querySelector('.page.back');
        if (bom) {
            let anchor = bom;
            pages.forEach((pg) => { anchor.parentNode.insertBefore(pg, anchor.nextSibling); anchor = pg; });
        } else {
            pages.forEach((pg) => { if (back) back.parentNode.insertBefore(pg, back); else doc.body.appendChild(pg); });
        }
    }
    return '<!doctype html>\n' + doc.documentElement.outerHTML;
}

// ── Pure PDF generator. Takes a pre-built var-map + render options instead of
// reading component state, so the Combined tab and the old Proposal tab share
// ONE PDF pipeline. Mirrors the original cscProposalTab.jsx generatePdf flow:
// template fetch (with CN/MY → EN body-sniff fallback), cscSubstitute +
// postProcessCscProposal, iframe + Poppins font-load + html2canvas/jsPDF loop,
// and pdf.save(...). Returns { ok: true } or { ok: false, error }.
export async function generateCscProposalPdf(map, { lang, includeSARE, includeSARE10, includeSARE15, includeOutright, includeLoan, includePhotos, hasYearBills = false, customerName, kwp, systemKwp, companyProfileOnly, bessHtml, loanTerms }) {
    try {
        // 90% generation guarantee note — only shown on the projection tables for
        // systems 54 kWp and above (per user 2026-06-29). systemKwp is the NUMERIC
        // total (combined for multi-meter); the `kwp` arg may be a label string.
        const _kwpNum = Number(systemKwp != null ? systemKwp : kwp) || 0;
        // Re-assert here too (in case the map was built without lang) — same helper.
        map.generation_guarantee_note = cscGenerationGuaranteeNote(lang, _kwpNum);
        const cacheBust = `?v=${Date.now()}`;
        const tplUrl = (lang === 'cn' ? '/templates/astern-proposal-csc-cn.html'
                      : lang === 'my' ? '/templates/astern-proposal-csc-my.html'
                      : '/templates/astern-proposal-csc.html') + cacheBust;
        let res = await fetch(tplUrl);
        let tpl = res.ok ? await res.text() : '';
        // Fallback to EN if the localized template doesn't exist OR if the
        // server returned the SPA index.html (prod nginx falls back to it
        // for missing static paths — body parses fine but has no .page divs).
        const looksLikeTemplate = (s) => /class\s*=\s*["']page(\s|["'])/.test(s);
        if (lang !== 'en' && (!res.ok || !looksLikeTemplate(tpl))) {
            res = await fetch(`/templates/astern-proposal-csc.html?v=${Date.now()}`);
            tpl = res.ok ? await res.text() : '';
        }
        if (!res.ok || !looksLikeTemplate(tpl)) {
            throw new Error(`Failed to fetch template (HTTP ${res.status})`);
        }
        const origin = window.location.origin;
        tpl = tpl
            .split("src='./").join(`src='${origin}/templates/`)
            .split('src="./').join(`src="${origin}/templates/`)
            .split("url('./").join(`url('${origin}/templates/`)
            .split('url("./').join(`url("${origin}/templates/`)
            .split('url(./').join(`url(${origin}/templates/`);
        // Loan terms are expanded on the RAW template, before substitution, so the clones
        // still carry their {{loanN_*}} placeholders for cscSubstitute to fill.
        const populated = postProcessCscProposal(
            cscSubstitute(expandLoanTerms(tpl, loanTerms, lang), map),
            { includeSARE, includeSARE10, includeSARE15, includeOutright, includeLoan, includePhotos, hasYearBills, companyProfileOnly, bessHtml, bessLang: lang });
        const iframe = document.createElement('iframe');
        iframe.style.cssText = 'position:fixed;left:-99999px;top:0;width:794px;height:1123px;border:0;';
        document.body.appendChild(iframe);
        const doc = iframe.contentDocument;
        doc.open(); doc.write(populated); doc.close();
        if (doc.fonts && doc.fonts.ready) { try { await doc.fonts.ready; } catch {} }
        // Poppins is loaded via <link> (Google Fonts); explicitly force-load
        // the weights we actually use in the cover/back-cover so the font
        // is GUARANTEED rasterized before html2canvas snapshots — otherwise
        // 300/400 fall back to a system font with broken glyphs in the PDF.
        if (doc.fonts && doc.fonts.load) {
            try {
                await Promise.all([
                    doc.fonts.load('300 13pt Poppins'),
                    doc.fonts.load('400 13pt Poppins'),
                    doc.fonts.load('500 8pt Poppins'),
                    doc.fonts.load('600 11pt Poppins'),
                    doc.fonts.load('700 13pt Poppins'),
                    doc.fonts.load('800 24pt Poppins'),
                ]);
            } catch {}
        }
        const imgs = Array.from(doc.images || []);
        await Promise.all(imgs.map((img) => {
            if (img.complete && img.naturalWidth > 0) return Promise.resolve();
            return new Promise((resolve) => {
                img.addEventListener('load', () => resolve(), { once: true });
                img.addEventListener('error', () => resolve(), { once: true });
                setTimeout(() => resolve(), 8000);
            });
        }));
        await new Promise((r) => setTimeout(r, 600));
        const pages = doc.querySelectorAll('.page');
        if (!pages.length) throw new Error('Template has no .page elements.');
        const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait', compress: false });
        for (let i = 0; i < pages.length; i++) {
            const canvas = await html2canvas(pages[i], { scale: 2.5, useCORS: true, logging: false, backgroundColor: '#ffffff', imageTimeout: 15000 });
            const img = canvas.toDataURL('image/jpeg', 0.98);
            if (i > 0) pdf.addPage();
            pdf.addImage(img, 'JPEG', 0, 0, 210, 297, undefined, 'FAST');
        }
        // Filename: "{Customer} - {kWp}KWP Astern Commercial Proposal.pdf"
        // Strip filesystem-illegal chars (/ \ : * ? " < > |) but keep spaces.
        const safeName = (customerName || 'Customer').replace(/[\/\\:*?"<>|]/g, '').trim();
        // Multi-meter callers pass a non-numeric label like "30+40"; keep it verbatim.
        // Single-meter callers pass a number → keep the existing numeric behaviour.
        const kwpLabel = (typeof kwp === 'string' && isNaN(Number(kwp))) ? kwp : String(Number(kwp) || 0);
        pdf.save(companyProfileOnly
            ? 'Astern Company Profile.pdf'
            : `${safeName} - ${kwpLabel}KWP Astern Commercial Proposal.pdf`);
        document.body.removeChild(iframe);
        return { ok: true };
    } catch (e) {
        console.error(e);
        return { ok: false, error: `Proposal failed: ${e.message || e}` };
    }
}
